import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

const IGNORED_CAMERA_ERRORS = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
]);

function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

function reportSelfieCaptureFailure(reason) {
  Sentry.captureMessage('IDENTITY_VALIDATION_SELFIE_CAPTURE_FAILED', {
    level: 'error',
    tags: {
      feature: 'identity_validation',
      action: 'capture_selfie',
    },
    extra: { reason },
  });
}

export default function useSelfieCamera({ enabled }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const stopCamera = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraReady(false);
    setIsStarting(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (!enabled) return false;
    setIsStarting(true);
    setCameraError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('UNSUPPORTED');
      Sentry.captureMessage('IDENTITY_VALIDATION_CAMERA_UNSUPPORTED', {
        level: 'warning',
        tags: {
          feature: 'identity_validation',
          action: 'start_camera',
        },
      });
      setIsStarting(false);
      return false;
    }

    try {
      stopStream(streamRef.current);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'user' }, width: { ideal: 1280 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCameraReady(true);
      return true;
    } catch (error) {
      stopStream(streamRef.current);
      streamRef.current = null;
      const errorName = error?.name || 'CAMERA_ERROR';
      setCameraError(errorName);
      if (!IGNORED_CAMERA_ERRORS.has(errorName)) {
        Sentry.captureException(error, {
          tags: {
            feature: 'identity_validation',
            action: 'start_camera',
          },
          extra: { errorName },
        });
      }
      return false;
    } finally {
      setIsStarting(false);
    }
  }, [enabled]);

  const capturePhoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !isCameraReady) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    if (!context) {
      reportSelfieCaptureFailure('NO_CANVAS_CONTEXT');
      return null;
    }
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    );
    if (!blob) {
      reportSelfieCaptureFailure('EMPTY_BLOB');
      return null;
    }
    return new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
  }, [isCameraReady]);

  useEffect(() => {
    if (!enabled) {
      stopCamera();
      return undefined;
    }
    startCamera();
    return stopCamera;
  }, [enabled, startCamera, stopCamera]);

  return {
    videoRef,
    isStarting,
    isCameraReady,
    cameraError,
    startCamera,
    stopCamera,
    capturePhoto,
  };
}
